/**
 * ReportsHome — unified reports catalog hub.
 * W17-ReportsCatalog-Hub
 */
import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import { getSession } from "@/lib/session";
import {
  REPORTS_CATALOG, AUDIENCE_ORDER, getCatalogByAudience,
  type ReportEntry, type ReportAudience, type ReportStatus,
} from "@/data/reports-catalog";
import ReportGlyph from "@/components/icons/reports/ReportGlyph";

const BLACK="#0B0B0F",SLATE="#6B6B70",BORDER="#E5E5E7",BG="#FAFAF7";
const STATUS_COLORS:Record<ReportStatus,{bg:string;text:string;border:string}>={
  LIVE:{bg:"#DCFCE7",text:"#15803D",border:"#86EFAC"},
  BETA:{bg:"#FEF9C3",text:"#92400E",border:"#FDE047"},
  "COMING SOON":{bg:"#F3F4F6",text:"#6B7280",border:"#D1D5DB"},
};
const AUDIENCE_LABELS:Record<ReportAudience,string>={
  AE:"AE Reports",BDR:"BDR Reports",Marketing:"Marketing Reports",
  Manager:"Manager Reports",Executive:"Executive Reports","Cross-role":"Cross-role / System-wide",
};
const ROLE_AUDIENCES:Record<string,ReportAudience[]>={
  ae:["AE","Cross-role"],bdr:["BDR","Cross-role"],marketing:["Marketing","Cross-role"],
  manager:["AE","BDR","Marketing","Manager","Executive","Cross-role"],
  executive:["AE","BDR","Marketing","Manager","Executive","Cross-role"],
  se:["AE","Cross-role"],
};

function exportPDF(n:string){const d=new Date().toLocaleDateString("en-CA");const o=document.title;document.title=`Mi6 Group · ${n} · ${d}`;window.print();document.title=o;}
function exportCSV(n:string,s:string){const d=new Date().toLocaleDateString("en-CA");const b=new Blob([`Report,Date\n"${n}","${d}"\n`],{type:"text/csv"});const u=URL.createObjectURL(b);const a=document.createElement("a");a.href=u;a.download=`${s}-${d}.csv`;a.click();URL.revokeObjectURL(u);}
function exportXLSX(n:string,s:string){import("xlsx").then(X=>{const d=new Date().toLocaleDateString("en-CA");const ws=X.utils.aoa_to_sheet([["Mi6 Group",n,d]]);const wb=X.utils.book_new();X.utils.book_append_sheet(wb,ws,"Report");X.writeFile(wb,`${s}-${d}.xlsx`);}).catch(()=>alert("Excel export unavailable — use CSV instead."));}

function StatusPill({status}:{status:ReportStatus}){const c=STATUS_COLORS[status];return(<span style={{display:"inline-flex",alignItems:"center",padding:"2px 8px",borderRadius:99,fontSize:10,fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",background:c.bg,color:c.text,border:`1px solid ${c.border}`,whiteSpace:"nowrap"}}>{status==="COMING SOON"?"Coming Soon":status}</span>);}

function ExportBtn({label,active,onClick}:{label:string;active:boolean;onClick?:()=>void}){
  return(<button title={active?`Export ${label}`:"Available when this report goes live"} onClick={active?onClick:undefined} style={{display:"inline-flex",alignItems:"center",gap:4,padding:"4px 8px",border:`1px solid ${active?BORDER:"#EFEFEE"}`,borderRadius:5,background:active?"#FFFFFF":"#F9FAFB",color:active?BLACK:"#C9C9CC",fontSize:10,fontWeight:700,letterSpacing:"0.05em",cursor:active?"pointer":"default",opacity:active?1:0.45}}>{label}</button>);
}

function ReportCard({report}:{report:ReportEntry}){
  const[,navigate]=useLocation();
  const isLive=report.status==="LIVE";
  const isBeta=report.status==="BETA";
  const isClickable=isLive||isBeta;
  function handleOpen(){if(!isClickable||!report.route)return;const r=report.route.includes(":")?"/accounts":report.route;navigate(r);}
  return(
    <div style={{background:"#FFFFFF",border:`1px solid ${BORDER}`,borderRadius:10,overflow:"hidden",display:"flex",flexDirection:"column",gap:0,boxShadow:"0 1px 3px rgba(15,23,42,0.04)",transition:"box-shadow 0.15s,border-color 0.15s"}}
      onMouseEnter={e=>{(e.currentTarget as HTMLElement).style.boxShadow="0 4px 12px rgba(15,23,42,0.09)";(e.currentTarget as HTMLElement).style.borderColor="#C9C9CC";}}
      onMouseLeave={e=>{(e.currentTarget as HTMLElement).style.boxShadow="0 1px 3px rgba(15,23,42,0.04)";(e.currentTarget as HTMLElement).style.borderColor=BORDER;}}>
      {/* W45.2 — Mi6 custom glyph hero band: full-width 80px above card content */}
      <div style={{width:"100%",height:80,display:"flex",alignItems:"center",justifyContent:"center",background:"#F8FAF9",borderBottom:`1px solid ${BORDER}`}}>
        <ReportGlyph reportId={report.id} status={report.status} size={48}/>
      </div>
      {/* Card body */}
      <div style={{padding:"14px 18px",display:"flex",flexDirection:"column",gap:10,flex:1}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
          <div style={{fontSize:14,fontWeight:800,color:BLACK,lineHeight:1.3,flex:1}}>{report.name}</div>
          <StatusPill status={report.status}/>
        </div>
        <div style={{fontSize:12,color:SLATE,lineHeight:1.55,flexGrow:1}}>{report.description}</div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,paddingTop:10,borderTop:"1px solid #EFEFEE",flexWrap:"wrap"}}>
          <button onClick={handleOpen} title={isClickable?`Open ${report.name}`:"Available when this report goes live"}
            style={{padding:"6px 14px",borderRadius:6,background:isClickable?BLACK:"#F3F4F6",color:isClickable?"#FFFFFF":"#9CA3AF",fontSize:11,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",border:"none",cursor:isClickable?"pointer":"default",opacity:isClickable?1:0.7}}>
            {isClickable?"Open":"Coming Soon"}
          </button>
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <ExportBtn label="PDF" active={isLive} onClick={()=>exportPDF(report.name)}/>
            <ExportBtn label="XLS" active={isLive&&report.exports.xlsx} onClick={()=>exportXLSX(report.name,report.id)}/>
            <ExportBtn label="CSV" active={isLive&&report.exports.csv} onClick={()=>exportCSV(report.name,report.id)}/>
          </div>
        </div>
      </div>
    </div>
  );
}

function AudienceSection({audience,reports}:{audience:ReportAudience;reports:ReportEntry[]}){
  const[open,setOpen]=useState(true);
  const liveCount=reports.filter(r=>r.status==="LIVE").length;
  return(
    <div style={{marginBottom:32}}>
      <button onClick={()=>setOpen(v=>!v)} style={{display:"flex",alignItems:"center",gap:10,width:"100%",background:"none",border:"none",cursor:"pointer",padding:"0 0 12px 0",borderBottom:`2px solid ${BLACK}`,marginBottom:16,textAlign:"left"}}>
        <span style={{fontSize:16,fontWeight:900,color:BLACK,letterSpacing:"-0.01em"}}>{AUDIENCE_LABELS[audience]}</span>
        <span style={{fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:SLATE,marginLeft:4}}>{liveCount} live · {reports.length} total</span>
        <span style={{marginLeft:"auto",fontSize:14,color:SLATE,userSelect:"none"}}>{open?"▲":"▼"}</span>
      </button>
      {open&&<div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16}} className="reports-grid">{reports.map(r=><ReportCard key={r.id} report={r}/>)}</div>}
    </div>
  );
}

export default function ReportsHome(){
  const session=getSession();
  const userRole=session?.role??"ae";
  const canSeeAll=userRole==="manager"||userRole==="executive";
  const[selectedRole,setSelectedRole]=useState<string>(userRole);
  const[search,setSearch]=useState("");
  const catalogByAudience=useMemo(()=>getCatalogByAudience(),[]);
  const visibleAudiences=useMemo<ReportAudience[]>(()=>{
    const a=ROLE_AUDIENCES[selectedRole]??AUDIENCE_ORDER;
    return AUDIENCE_ORDER.filter(x=>a.includes(x));
  },[selectedRole]);
  const filtered=useMemo(()=>{
    const q=search.trim().toLowerCase();
    const r:Record<ReportAudience,ReportEntry[]>={} as any;
    for(const a of visibleAudiences){const e=catalogByAudience[a]??[];r[a]=q?e.filter(x=>x.name.toLowerCase().includes(q)):e;}
    return r;
  },[visibleAudiences,catalogByAudience,search]);
  const totalVisible=visibleAudiences.reduce((n,a)=>n+(filtered[a]?.length??0),0);
  const roles=[{key:"ae",label:"AE"},{key:"bdr",label:"BDR"},{key:"marketing",label:"Marketing"},{key:"manager",label:"Manager"},{key:"executive",label:"Executive"}];

  return(<>
    <style>{`@media print{nav,aside,[data-sidebar],[data-topbar],.no-print{display:none!important}body{background:#fff}.reports-grid{grid-template-columns:1fr 1fr!important}}@media(max-width:900px){.reports-grid{grid-template-columns:1fr 1fr!important}}@media(max-width:600px){.reports-grid{grid-template-columns:1fr!important}}`}</style>
    <OperatorShell breadcrumbs={[{label:"Reports"}]}>
      <div style={{maxWidth:1200,margin:"0 auto"}}>
        <div style={{marginBottom:28}}>
          <h1 style={{fontSize:26,fontWeight:900,color:BLACK,margin:"0 0 6px",letterSpacing:"-0.01em"}}>Reports &amp; Dashboards</h1>
          <div style={{fontSize:14,color:SLATE}}>Full catalog of every report and dashboard in the Mi6 intelligence system. LIVE reports are ready now. Coming Soon reports signal what is next on the roadmap.</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:32,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
            <span style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",color:SLATE,marginRight:4}}>Role</span>
            {roles.map(({key,label})=>{
              if(!canSeeAll&&key!==userRole)return null;
              const active=selectedRole===key;
              return(<button key={key} onClick={()=>setSelectedRole(key)} style={{padding:"5px 12px",borderRadius:99,border:`1px solid ${active?BLACK:BORDER}`,background:active?BLACK:"#FFFFFF",color:active?"#FFFFFF":SLATE,fontSize:11,fontWeight:700,letterSpacing:"0.04em",cursor:"pointer"}}>{label}</button>);
            })}
          </div>
          <div style={{flex:1}}/>
          <div style={{position:"relative"}}>
            <input type="text" placeholder="Search reports..." value={search} onChange={e=>setSearch(e.currentTarget.value)}
              style={{padding:"8px 12px 8px 32px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,color:BLACK,background:"#FFFFFF",outline:"none",width:220,boxSizing:"border-box"}}/>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>
              <circle cx="6.5" cy="6.5" r="4.5" stroke={SLATE} strokeWidth="1.5"/><path d="M10.5 10.5l3.5 3.5" stroke={SLATE} strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:16,padding:"10px 16px",background:BG,border:`1px solid ${BORDER}`,borderRadius:8,marginBottom:32,fontSize:12,color:SLATE}}>
          <span><strong style={{color:BLACK,fontWeight:800}}>{REPORTS_CATALOG.filter(r=>r.status==="LIVE").length}</strong> live</span>
          <span style={{color:BORDER}}>|</span>
          <span><strong style={{color:"#92400E",fontWeight:800}}>{REPORTS_CATALOG.filter(r=>r.status==="BETA").length}</strong> beta</span>
          <span style={{color:BORDER}}>|</span>
          <span><strong style={{color:BLACK,fontWeight:800}}>{REPORTS_CATALOG.filter(r=>r.status==="COMING SOON").length}</strong> coming soon</span>
          <span style={{color:BORDER}}>|</span>
          <span><strong style={{color:BLACK,fontWeight:800}}>{REPORTS_CATALOG.length}</strong> total</span>
          {search&&<><span style={{color:BORDER}}>|</span><span><strong style={{color:BLACK}}>{totalVisible}</strong> matching</span></>}
        </div>
        {search&&totalVisible===0&&<div style={{padding:48,textAlign:"center",background:"#FFFFFF",border:`1px solid ${BORDER}`,borderRadius:8,marginBottom:32}}><div style={{fontSize:14,color:SLATE}}>No reports match &ldquo;{search}&rdquo;. Try a shorter term.</div></div>}
        {visibleAudiences.map(aud=>{const e=filtered[aud]??[];if(!e.length)return null;return<AudienceSection key={aud} audience={aud} reports={e}/>;} )}
      </div>
    </OperatorShell>
  </>);
}
